// File: clientapp/src/components/Header.js
import React, { useContext, useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Badge,
    Modal,
    Box,
    Popover,
    Avatar,
    Button,
    List,
    ListItemButton,
    ListItemText
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';            // ← import the Chat (assistant) icon
import { Link, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from '../contexts/AuthContext';
import { startConnection } from '../services/signalrService';
import * as signalR from '@microsoft/signalr';

// Inline styles for modals
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: 350,
    minHeight: 250,
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    outline: 'none',
};

const closeIconStyle = {
    position: 'absolute',
    top: 8,
    right: 8,
    cursor: 'pointer',
};

const Header = ({ onMenuClick }) => {
    const { isAuthenticated, user, features, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [accountModalOpen, setAccountModalOpen] = useState(false);
    const [adminModalOpen, setAdminModalOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notificationCount, setNotificationCount] = useState(0);
    const [notifAnchorEl, setNotifAnchorEl] = useState(null);
    const [connection, setConnection] = useState(null);

    const userDisplayName = user?.userName || user?.email || 'User';
    const userInitial = userDisplayName.charAt(0).toUpperCase();
    const [brand, setBrand] = useState("MODMAI"); // default fallback

    // Fetch brand from API.
    useEffect(() => {
        async function fetchBrand() {
            try {
                const response = await fetch('/api/sitesetup/brand', {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (response.ok) {
                    const brandText = await response.text();
                    setBrand(brandText);
                }
            } catch (err) {
                console.error("Error fetching site brand:", err);
            }
        }
        fetchBrand();
    }, []);

    // Load unread notifications.
    useEffect(() => {
        async function loadUnreadNotifications() {
            try {
                const response = await fetch(`/api/notifications?onlyUnread=true`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (response.ok) {
                    const data = await response.json();
                    setNotificationCount(data.notifications ? data.notifications.length : 0);
                    setNotifications(data.notifications || []);
                }
            } catch (error) {
                console.error("Error loading notifications:", error);
            }
        }
        loadUnreadNotifications();
    }, []);

    // Listen for clearNotifications event to clear local header notifications.
    useEffect(() => {
        const handleClear = () => {
            setNotifications([]);
            setNotificationCount(0);
        };
        document.addEventListener("clearNotifications", handleClear);
        return () => document.removeEventListener("clearNotifications", handleClear);
    }, []);

    // Initialize SignalR connection using the central service.
    useEffect(() => {
        async function initConnection() {
            const conn = await startConnection();
            setConnection(conn);
            // Subscribe to notifications.
            conn.on("ReceiveNotification", (message) => {
                console.log("Received notification:", message);
                setNotificationCount(prev => prev + 1);
                setNotifications(prev => [message, ...prev]);
            });
        }
        initConnection();
    }, []);

    const handleNotifClick = (event) => {
        setNotifAnchorEl(event.currentTarget);
        // Optionally, reset the notification count.
        setNotificationCount(0);
    };

    const handleNotifClose = () => {
        setNotifAnchorEl(null);
    };

    const notifPopoverOpen = Boolean(notifAnchorEl);
    const notifPopoverId = notifPopoverOpen ? 'notification-popover' : undefined;

    const clearAllNotifications = async () => {
        try {
            const response = await fetch(`/api/notifications/markallasread`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                // Dispatch event to clear notifications in both header and page.
                document.dispatchEvent(new CustomEvent("clearNotifications"));
            }
        } catch (error) {
            console.error("Error clearing notifications:", error);
        }
    };

    const handleLogout = async () => {
        try {
            await fetch('/api/account/logout', {
                method: 'POST',
                credentials: 'include'
            });
        } catch (error) {
            console.error("Logout error:", error);
        }

        logout(); // clears React context
        window.location.href = '/login'; // hard redirect for clean slate
    };

    return (
        <>
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, height: 40, backgroundColor: 'rgb(28,28,28)' }}>
                <Toolbar
                    variant="dense"
                    disableGutters
                    sx={{
                        height: '40px !important',
                        minHeight: '40px !important',
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={onMenuClick}
                        sx={{
                            width: 48,
                            height: 40,
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <MenuIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Link to="/" style={{ textDecoration: 'none', color: '#fff' }}>
                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{ fontSize: 18, display: 'flex', alignItems: 'center', color: '#fff' }}
                            dangerouslySetInnerHTML={{ __html: brand }}
                        />
                    </Link>
                    <Box sx={{ flexGrow: 1 }} />
                    {isAuthenticated && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* ────────────────────────────────────────────── */}
                            {/* Show “Assistant” chat icon if user has the feature */}
                            {features?.includes("Use AI Assistant") && (
                                <IconButton
                                    color="inherit"
                                    onClick={() => navigate('/assistant')}
                                    sx={{ mr: 1 }}
                                >
                                    <ChatIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            )}
                            {/* ────────────────────────────────────────────── */}

                            {features && features.includes("AdminAccess") && (
                                <IconButton color="inherit" onClick={() => setAdminModalOpen(true)}>
                                    <SettingsIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            )}
                            <IconButton color="inherit" onClick={handleNotifClick}>
                                <Badge badgeContent={notificationCount} color="error">
                                    <NotificationsIcon sx={{ fontSize: 20 }} />
                                </Badge>
                            </IconButton>
                            <IconButton color="inherit" onClick={() => setAccountModalOpen(true)}>
                                <Avatar sx={{ width: 24, height: 24 }}>{userInitial}</Avatar>
                            </IconButton>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>

            {/* Notifications Popover */}
            <Popover
                id={notifPopoverId}
                open={notifPopoverOpen}
                anchorEl={notifAnchorEl}
                onClose={handleNotifClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box p={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Button component={Link} to="/notifications" onClick={handleNotifClose} size="small">
                            View All Notifications
                        </Button>
                        {/* Small Clear All link */}
                        <Button onClick={clearAllNotifications} size="small" sx={{ textTransform: 'none' }}>
                            Clear All
                        </Button>
                    </Box>
                    <Typography variant="subtitle1">Notifications</Typography>
                    {notifications.length === 0 ? (
                        <Typography variant="body2">No notifications</Typography>
                    ) : (
                        <List>
                            {notifications.map((note, index) => (
                                <ListItemButton key={index} component={Link} to="/notifications">
                                    <ListItemText primary={typeof note === "object" ? note.message : note} />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Box>
            </Popover>

            {/* Account Modal */}
            <Modal open={accountModalOpen} onClose={() => setAccountModalOpen(false)}>
                <Box sx={modalStyle}>
                    <IconButton sx={closeIconStyle} onClick={() => setAccountModalOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                    <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                        <Avatar sx={{ width: 64, height: 64, mb: 1 }}>{userInitial}</Avatar>
                        <Typography variant="h6" align="center">
                            {userDisplayName}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                            navigate("/account");
                            setAccountModalOpen(false);
                        }}
                    >
                        My Account
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                            setAccountModalOpen(false);
                            handleLogout();
                        }}
                        sx={{ mt: 1 }}
                    >
                        Sign Out
                    </Button>
                </Box>
            </Modal>

            {/* Admin Modal */}
            <Modal open={adminModalOpen} onClose={() => setAdminModalOpen(false)}>
                <Box sx={modalStyle}>
                    <IconButton sx={closeIconStyle} onClick={() => setAdminModalOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" mb={2}>
                        Administration
                    </Typography>
                    <List>
                        <ListItemButton component={Link} to="/security" onClick={() => setAdminModalOpen(false)}>
                            <ListItemText primary="Security Admin" />
                        </ListItemButton>
                        <ListItemButton component={Link} to="/jobs" onClick={() => setAdminModalOpen(false)}>
                            <ListItemText primary="Scheduled Jobs" />
                        </ListItemButton>
                        {features.includes("Manage connections") && (
                            <ListItemButton component={Link} to="/connections" onClick={() => setAdminModalOpen(false)}>
                                <ListItemText primary="Connections" />
                            </ListItemButton>
                        )}
                        {features.includes("Manage mappings") && (
                            <ListItemButton component={Link} to="/mappings" onClick={() => setAdminModalOpen(false)}>
                                <ListItemText primary="Mappings" />
                            </ListItemButton>
                        )}
                        {/* Additional admin links */}
                    </List>
                </Box>
            </Modal>
        </>
    );
};

export default Header;
